import React, { useRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form, Formik, useFormikContext } from 'formik';
import * as yup from 'yup';
import i18n from 'i18n-js';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Box, alpha, Tabs, Tab } from '@material-ui/core';
import RestaurantIcon from '@material-ui/icons/Restaurant';
import clsx from 'clsx';

import { default as restaurantStore } from 'store/restaurantDetailsStore';
import { CatalogDishData, DishCreateModel } from 'api/models';
import categoryStore from 'store/categoryStore';
import { AdditionalDishSize, DishData, SectionData } from 'store/menuDetailsStore/reducer';
import { trimLeftAndRight } from 'helpers/helperFunctions';
import { ImageContainerRefProps } from 'components/shared/components/ImageContainer';
import { TaxType, Positions, SpicinessLevels, Diets, DietaryLaws, MenuType } from 'constants/enums';
import { useStyles } from '../../styles';
import { useAppSelector } from 'index';
import { regexHelper } from 'helpers/regexHelper';
import { getAllPricingLevels } from 'helpers/pricingLevelHelper';
import {
    formatPickupTime,
    getDefaultCustomOffer,
    getDefaultSameDayOffer,
    getInitialOfferWindow,
    getTakeAwayPickupRange,
    getWeekMinutes,
} from 'helpers/orderInAdvanceHelper';
import TextEditor from 'components/shared/components/TextEditor';
import SetFieldText from 'components/shared/components/SetFieldText';
import menusStore from 'store/menusStore';
import { theme } from 'config/theme';

import MainTab from './components/MainTab';
import AdvancedTab from './components/AdvancedTab';
import PriceManagementTab from './components/PriceManagementTab';

export enum DishManagementTabs {
    MAIN,
    ADVANCED,
    PRICE_MANAGEMENT,
}

export function getDaysSelectOptions(lang: string) {
    const isDE = lang === 'de';
    return [
        { value: 'Monday', label: 'MO' },
        { value: 'Tuesday', label: isDE ? 'DI' : 'TU' },
        { value: 'Wednesday', label: isDE ? 'MI' : 'WE' },
        { value: 'Thursday', label: isDE ? 'DO' : 'TH' },
        { value: 'Friday', label: 'FR' },
        { value: 'Saturday', label: 'SA' },
        { value: 'Sunday', label: isDE ? 'SO' : 'SU' },
    ];
}

export interface DishManagementDialogProps {
    dish: DishData;
    disabled: boolean;
    sections: SectionData[];
    action: (model: DishCreateModel, id?: string, sectionId?: string) => void | Promise<void>;
    isOpen: boolean;
    onClose: (event?: React.MouseEvent<HTMLElement>) => void;
    forCopy?: boolean;
    restaurantName?: string;
}

export default function DishManagementDialog(props: DishManagementDialogProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const categoryDetails = useAppSelector((state) => state.category.categoryDetails);
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const user = useAppSelector((state) => state.user);
    const taxes = useAppSelector((state) => state.restaurantDetails.taxes);
    const lang = useAppSelector((state) => state.global.language);
    const menu = useAppSelector((state) => state.menuDetails.menu);
    const dishes = useRef<string[]>([]);
    const childRef = useRef<ImageContainerRefProps>(null);
    const [showAddIngredientsModal, setShowAddIngredientsModal] = useState(false);
    const [isDeniedTaxSetup, setIsDeniedTaxSetup] = useState(true);
    const [showAdditionalForm, setShowAdditionalForm] = useState(false);
    const [advancedSettingsHasError, setAdvancedSettingsHasError] = useState(false);
    const [showEditor, setShowEditor] = useState(false);
    const [textToEdit, setTextToEdit] = useState('');
    const [valueName, setValueName] = useState('');
    const [tabActive, setTab] = useState<DishManagementTabs>(DishManagementTabs.MAIN);
    const title = useRef<HTMLDivElement>(null);
    const currentRestaurant = user.restaurants?.find((x) => x.id === user.currentRestaurantId);
    const currentPosition = currentRestaurant?.currentPosition;
    const isCatalogManager =
        (menu.type === MenuType.Catalog &&
            currentPosition === Positions.MANAGER &&
            menu.catalogData?.allowToEditCatalog) ??
        false;
    const isCatalog = menu.type === MenuType.Catalog;
    const dishList = useAppSelector((state) => state.menus.dishList);

    function handleChangeTab(event: React.ChangeEvent<{}>, view: number) {
        console.log(view);
        if (view !== tabActive) {
            setTab(view);
        }
    }

    function saveTextFromEditor(text: string) {
        setTextToEdit(text);
    }

    function preventProp(event: React.MouseEvent<HTMLElement>) {
        event.stopPropagation();
    }

    function onCancel() {
        setShowAdditionalForm(false);
        props.onClose();
    }

    function handleShowAdvancedSettingsError(hasError: boolean) {
        setAdvancedSettingsHasError(hasError);
    }

    const editedRestaurantId = (props.dish as CatalogDishData).restaurantId;
    const isOtherRestaurantItem = Boolean(
        editedRestaurantId && editedRestaurantId !== user.currentRestaurantId
    );
    const levelsToSeed = isOtherRestaurantItem
        ? restaurant.groupPricingLevels ?? []
        : getAllPricingLevels(restaurant, isCatalog);
    const knownLevelIds = new Set(
        getAllPricingLevels(restaurant, isCatalog).map((level) => level.id)
    );

    function preparePricelevels(additionalSize?: AdditionalDishSize) {
        let priceLevels: {
            price: string;
            priceLevelId: string;
        }[] = [];
        const persistedLevels = additionalSize
            ? additionalSize.priceLevels
            : props.dish?.priceLevels;

        persistedLevels
            ?.filter((pl) => isOtherRestaurantItem || knownLevelIds.has(pl.priceLevelId))
            .forEach((pl) =>
                priceLevels.push({ price: pl.price.toFixed(2), priceLevelId: pl.priceLevelId })
            );

        levelsToSeed.forEach((pl) => {
            if (priceLevels.every((pr) => pr.priceLevelId !== pl.id)) {
                priceLevels.push({ priceLevelId: pl.id, price: props.dish.price.toFixed(2) });
            }
        });

        return priceLevels;
    }

    useEffect(() => {
        setIsDeniedTaxSetup(
            user.restaurants.find((x) => x.id === user.currentRestaurantId)?.currentPosition !==
                Positions.MANAGER
        );
        if (!restaurant.id) {
            dispatch(restaurantStore.actionCreators.getRestaurantInformation());
        }
        if (!categoryDetails) {
            dispatch(categoryStore.actionCreators.getCategoryDetails());
        }
    }, [categoryDetails]);

    useEffect(() => {
        if (props.isOpen && !!!taxes.id) {
            dispatch(restaurantStore.actionCreators.getTaxes());
        }

        if (props.isOpen) {
            dispatch(menusStore.actionCreators.getDishList());
        }
    }, [props.isOpen]);

    useEffect(() => {
        if (props.dish.catalogData?.catalogId) {
            const currentCatalogDishes = menu.sections
                .flatMap((s) => s.dishes)
                .filter((dish) => dish.id !== props.dish.id);
            const additionalCatalogDishes = dishList.filter(
                (d) =>
                    d.id !== props.dish.id &&
                    d.catalogData?.catalogId === props.dish.catalogData?.catalogId
            );
            dishes.current = Array.from(
                new Set(
                    [...currentCatalogDishes, ...additionalCatalogDishes].map((dish) =>
                        dish.name.toUpperCase()
                    )
                )
            );
        } else {
            // check all non-catalog dishes
            dishes.current = dishList
                .filter((d) => d.id !== props.dish.id && !d.catalogData?.catalogId)
                .map((dish) => dish.name.toUpperCase());
        }
    }, [dishList]);

    function prepareSameDayOffer(offer: any) {
        if (!offer) {
            return restaurant.isOrderInAdvance
                ? getDefaultSameDayOffer(restaurant.takeAway?.workingHours)
                : null;
        }
        return {
            ...offer,
            isEnabled: !!offer.isEnabled,
            orderingWindow: getInitialOfferWindow(
                offer.orderingWindow,
                offer.orderingFollowsWorkingHours,
                restaurant.takeAway?.workingHours
            ),
            pickupWindow: getInitialOfferWindow(
                offer.pickupWindow,
                offer.pickupFollowsWorkingHours,
                restaurant.takeAway?.workingHours
            ),
        };
    }

    function prepareCustomOffer(offer: any) {
        if (!offer) {
            return restaurant.isOrderInAdvance
                ? getDefaultCustomOffer(restaurant.takeAway?.workingHours)
                : null;
        }
        return {
            ...prepareSameDayOffer(offer),
            // The API returns WeekDays as PascalCase ("Tuesday"); the day Selects use lowercase values.
            daySettings: (offer.daySettings ?? []).map((daySetting: any) => ({
                ...daySetting,
                fromDay: daySetting.fromDay
                    ? String(daySetting.fromDay).toLowerCase()
                    : daySetting.fromDay,
                toDay: daySetting.toDay ? String(daySetting.toDay).toLowerCase() : daySetting.toDay,
            })),
        };
    }

    function serializeOffer(offer: any, isCustom: boolean, orderingOffsetHours?: number) {
        if (!offer) {
            return undefined;
        }

        const serializeWindow = (window: any) =>
            window?.from != null && window?.to != null ? window : null;

        return {
            ...offer,
            discountPercent: Number(offer.discountPercent || 0),
            orderingOffsetHours: Number(orderingOffsetHours ?? offer.orderingOffsetHours ?? 0),
            stockQuantity: offer.stockQuantity != null ? Number(offer.stockQuantity) : null,
            orderingWindow: serializeWindow(offer.orderingWindow),
            pickupWindow: serializeWindow(offer.pickupWindow),
            ...(isCustom
                ? {
                      daySettings: (offer.daySettings ?? []).map((daySetting: any) => ({
                          fromDay: daySetting.fromDay,
                          from: daySetting.from,
                          toDay: daySetting.toDay,
                          to: daySetting.to,
                      })),
                  }
                : {}),
        };
    }

    const initialFormValues = {
        name: props.forCopy ? 'copy of ' + props.dish.name : props.dish.name,
        shortName: props.dish.shortName,
        description: props.dish.description,
        shortDescription: props.dish.shortDescription,
        price: props.dish.price!.toFixed(2),
        photo: props.dish.photo,
        section: props.dish.sectionId || '',
        addToGallery: props.dish.addToGallery,
        category: props.dish.categoryId || '',
        photoSelected: props.dish.photo.length > 0,
        allergens: props.dish.allergens,
        ingredients: props.dish.ingredients,
        primeCost: props.dish.primeCost?.toFixed(2) ?? 0,
        dineInTax: props.dish.dineInTax ?? TaxType.None,
        homeDeliveryTax: props.dish.homeDeliveryTax ?? TaxType.None,
        takeAwayTax: props.dish.takeAwayTax ?? TaxType.None,
        barcode: props.dish.barcode,
        stockQuantity: props.dish.stockQuantity,
        minimumStock: props.dish.minimumStock,
        notFood: props.dish.notFood,
        color: props.dish.color,
        spicinessLevel: props.dish.spicinessLevel || SpicinessLevels.None,
        diet: props.dish.diet || Diets.None,
        dietaryLaw: props.dish.dietaryLaw || DietaryLaws.None,
        labels: props.dish.labels?.map((label) => label.id) || [],
        size: props.dish.size,
        additionalSizes: props.dish.additionalSizes.map((x) => ({
            ...x,
            price: x.price.toFixed(2),
            priceLevels: preparePricelevels(x),
            orderInAdvanceSameDay: prepareSameDayOffer(x.orderInAdvanceSameDay),
            orderInAdvanceCustom: prepareCustomOffer(x.orderInAdvanceCustom),
        })),
        priceLevels: preparePricelevels(),
        isCustomPrice: !!props.dish.isCustomPrice,
        externalId: props.dish.externalId?.toString(),
        supplierName: props.dish.supplierName,
        orderInAdvanceSameDay: prepareSameDayOffer(props.dish.orderInAdvanceSameDay),
        orderInAdvanceCustom: prepareCustomOffer(props.dish.orderInAdvanceCustom),
    };

    function validateTaxes(name: string, values: any) {
        switch (name) {
            case 'dineInTax':
                if (!restaurant.dineIn.isActive) {
                    return true;
                }
                break;
            case 'homeDeliveryTax':
                if (!restaurant.homeDelivery.isActive) {
                    return true;
                }
                break;
            case 'takeAwayTax':
                if (!restaurant.takeAway.isActive) {
                    return true;
                }
                break;
            default:
                return true;
        }
        if (restaurant.dineIn.isActive && values.dineInTax !== TaxType.None) {
            return true;
        }
        if (restaurant.takeAway.isActive && values.takeAwayTax !== TaxType.None) {
            return true;
        }
        if (restaurant.homeDelivery.isActive && values.homeDeliveryTax !== TaxType.None) {
            return true;
        }
        return false;
    }

    function isFieldDisabled(name: string, additionalOption: boolean = false) {
        if (name === 'save') {
            if (isCatalog && isCatalogManager && !!props.restaurantName) {
                return false;
            }
            return props.disabled;
        }

        if (name === 'stockQuantity') {
            return currentPosition !== Positions.MANAGER;
        }
        if (['externalId', 'supplierName', 'category'].includes(name)) {
            return false;
        }
        if (name === 'price') {
            if (props.restaurantName && isCatalogManager) {
                return false;
            }
            return props.disabled || additionalOption || menu.type === MenuType.MultiCourse;
        }
        if (name === 'priceLevel' && props.restaurantName && isCatalogManager) {
            return false;
        }
        return props.disabled || (isCatalog && !isCatalogManager);
    }

    const timeRangeSchema = yup
        .object()
        .nullable()
        .shape({
            from: yup.number().nullable().typeError('Enter complete time (HH:mm)'),
            to: yup.number().nullable().typeError('Enter complete time (HH:mm)'),
        })
        .test(
            'offer-hours-required',
            i18n.t('common.pickupHoursRequiredForEnabledSize'),
            function (value: { from?: number | null; to?: number | null } | null | undefined) {
                return value == null || (value.from != null && value.to != null);
            }
        )
        .test(
            'offer-hours-order',
            i18n.t('common.pickupHoursEndAfterStart'),
            function (value: { from?: number | null; to?: number | null } | null | undefined) {
                if (value?.from == null || value?.to == null) {
                    return true;
                }
                return value.to > value.from;
            }
        );

    // A recurring weekly ordering window (may cross the end of the week); from/to are only
    // required when the surrounding offer doesn't follow the restaurant working hours - the
    // "required"/"not zero length" checks live on the daySettings array test below, since only
    // there do we have access to the sibling orderingFollowsWorkingHours flag.
    const daySettingSchema = yup.object().shape({
        fromDay: yup.string().required(),
        toDay: yup.string().required(),
        from: yup.number().nullable().typeError(i18n.t('common.pickupHoursRequiredForEnabledSize')),
        to: yup.number().nullable().typeError(i18n.t('common.pickupHoursRequiredForEnabledSize')),
    });

    const offerSchema = yup.object().shape({
        isEnabled: yup.boolean(),
        discountPercent: yup
            .number()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .typeError(i18n.t('common.enterPercentageBetweenZeroAndHundred'))
            .min(0, i18n.t('common.enterPercentageBetweenZeroAndHundred'))
            .max(100, i18n.t('common.enterPercentageBetweenZeroAndHundred'))
            .test('discount-decimals', i18n.t('common.useAtMostTwoDecimals'), (value) =>
                value == null ? true : regexHelper.twoDigisAfterComma.test(String(value))
            ),
        orderingOffsetHours: yup
            .number()
            .integer(i18n.t('form.errors.onlyWholeNumbers'))
            .min(0)
            .max(12),
        stockQuantity: yup
            .number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .integer(i18n.t('form.errors.onlyWholeNumbers'))
            .nullable()
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero')),
        orderingWindow: timeRangeSchema.test(
            'manual-ordering-window-required',
            i18n.t('common.pickupHoursRequiredForEnabledSize'),
            function (value) {
                const offer = this.parent as any;
                return (
                    !offer?.isEnabled ||
                    offer.orderingFollowsWorkingHours ||
                    (value?.from != null && value?.to != null && value.to > value.from)
                );
            }
        ),
        pickupWindow: timeRangeSchema.test(
            'manual-pickup-window-required',
            i18n.t('common.pickupHoursRequiredForEnabledSize'),
            function (value) {
                const offer = this.parent as any;
                return (
                    !offer?.isEnabled ||
                    offer.pickupFollowsWorkingHours ||
                    (value?.from != null && value?.to != null && value.to > value.from)
                );
            }
        ),
    });

    // Manual same-day ordering hours must never reach outside the restaurant's take-away hours.
    const workingHoursRange = getTakeAwayPickupRange(restaurant.takeAway?.workingHours);
    const sameDayOfferSchema = offerSchema.shape({
        orderingWindow: offerSchema.fields.orderingWindow.test(
            'ordering-window-within-working-hours',
            i18n.t('common.orderingWindowOutsideWorkingHours', {
                from:
                    workingHoursRange.from != null ? formatPickupTime(workingHoursRange.from) : '-',
                to: workingHoursRange.to != null ? formatPickupTime(workingHoursRange.to) : '-',
            }),
            function (value: { from?: number | null; to?: number | null } | null | undefined) {
                const offer = this.parent as any;
                if (
                    !offer?.isEnabled ||
                    offer.orderingFollowsWorkingHours ||
                    value?.from == null ||
                    value?.to == null ||
                    workingHoursRange.from == null ||
                    workingHoursRange.to == null
                ) {
                    return true;
                }
                return value.from >= workingHoursRange.from && value.to <= workingHoursRange.to;
            }
        ),
    });

    const validationSchema = yup.object().shape({
        name: yup
            .string()
            .required(
                i18n.t('form.errors.required', {
                    name: 'Name',
                })
            )
            .test(
                'existsCheck',
                i18n.t('form.errors.alreadyTaken'),
                (value: any) => !dishes.current.includes(value?.trim().toUpperCase())
            ),
        shortName: yup.string().required(
            i18n.t('form.errors.required', {
                name: 'Short name',
            })
        ),
        price: yup
            .number()
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero'))
            .test('is-decimal', i18n.t('form.errors.twoDigitsAfterComma'), (val?: number) => {
                if (val) {
                    return regexHelper.twoDigisAfterComma.test(val.toString());
                }
                return true;
            }),
        primeCost: yup
            .number()
            .test('is-decimal', i18n.t('form.errors.twoDigitsAfterComma'), (val?: number) => {
                if (val) {
                    return regexHelper.twoDigisAfterComma.test(val.toString());
                }
                return true;
            })
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero')),
        category: (() => {
            return props.restaurantName
                ? yup.string().notRequired()
                : yup.string().required(
                      i18n.t('form.errors.required', {
                          name: 'Work Area',
                      })
                  );
        })(),
        section: (() => {
            return props.restaurantName
                ? yup.string().notRequired()
                : yup.string().required(
                      i18n.t('form.errors.required', {
                          name: 'Section',
                      })
                  );
        })(),
        dineInTax: yup
            .string()
            .test(
                'selectsCheck',
                i18n.t('form.errors.atLeasOneTaxSelected'),
                (value: any, context: any) => validateTaxes('dineInTax', context.parent)
            ),
        homeDeliveryTax: yup
            .string()
            .test(
                'selectsCheck',
                i18n.t('form.errors.atLeasOneTaxSelected'),
                (value: any, context: any) => validateTaxes('homeDeliveryTax', context.parent)
            ),
        takeAwayTax: yup
            .string()
            .test(
                'selectsCheck',
                i18n.t('form.errors.atLeasOneTaxSelected'),
                (value: any, context: any) => validateTaxes('takeAwayTax', context.parent)
            ),
        barcode: yup
            .string()
            .matches(
                regexHelper.lettersAndNumbersOnly,
                i18n.t('form.errors.lettersAndNumbersOnly')
            ),
        stockQuantity: yup
            .number()
            .integer(i18n.t('form.errors.onlyWholeNumbers'))
            .nullable()
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero')),
        minimumStock: yup
            .number()
            .integer(i18n.t('form.errors.onlyWholeNumbers'))
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero')),
        size: yup.string().when('additionalSizes', {
            is: (value: []) => !!value.length,
            then: yup.string().required(
                i18n.t('form.errors.required', {
                    name: 'Size',
                })
            ),
            otherwise: yup.string().notRequired(),
        }),
        orderInAdvanceSameDay: restaurant.isOrderInAdvance
            ? sameDayOfferSchema
                  .nullable()
                  .test(
                      'at-least-one-enabled-size',
                      i18n.t('common.preOrderAtLeastOneSizeEnabled'),
                      function (this: any, value: any) {
                          if (!value?.isEnabled) return true;
                          return [
                              value,
                              ...(this.parent?.additionalSizes ?? []).map(
                                  (size: any) => size.orderInAdvanceSameDay
                              ),
                          ].some((offer: any) => offer?.isEnabled);
                      }
                  )
            : yup.mixed(),
        orderInAdvanceCustom: restaurant.isOrderInAdvance
            ? offerSchema
                  .shape({
                      daySettings: yup
                          .array()
                          .of(daySettingSchema)
                          .test(
                              'custom-window-required',
                              i18n.t('common.addAtLeastOneOrderingWindow'),
                              function (value) {
                                  return !this.parent?.isEnabled || !!value?.length;
                              }
                          )
                          .test(
                              'custom-window-times-required',
                              i18n.t('common.pickupHoursRequiredForEnabledSize'),
                              function (value) {
                                  const offer = this.parent as any;
                                  if (!offer?.isEnabled || offer.orderingFollowsWorkingHours) {
                                      return true;
                                  }
                                  return (value ?? []).every(
                                      (window: any) => window?.from != null && window?.to != null
                                  );
                              }
                          )
                          .test(
                              'custom-window-not-zero-length',
                              i18n.t('common.orderingWindowZeroLength'),
                              function (value) {
                                  const offer = this.parent as any;
                                  if (!offer?.isEnabled || offer.orderingFollowsWorkingHours) {
                                      return true;
                                  }
                                  return (value ?? []).every((window: any) => {
                                      if (window?.from == null || window?.to == null) {
                                          return true;
                                      }
                                      return (
                                          getWeekMinutes(window.fromDay, window.from) !==
                                          getWeekMinutes(window.toDay, window.to)
                                      );
                                  });
                              }
                          ),
                  })
                  .nullable()
                  .test(
                      'at-least-one-enabled-size',
                      i18n.t('common.preOrderAtLeastOneSizeEnabled'),
                      function (this: any, value: any) {
                          if (!value?.isEnabled) return true;
                          return [
                              value,
                              ...(this.parent?.additionalSizes ?? []).map(
                                  (size: any) => size.orderInAdvanceCustom
                              ),
                          ].some((offer: any) => offer?.isEnabled);
                      }
                  )
            : yup.mixed(),
        additionalSizes: yup.array().of(
            yup.object().shape({
                size: yup.string().required(
                    i18n.t('form.errors.required', {
                        name: i18n.t('common.size'),
                    })
                ),
                price: yup
                    .number()
                    .typeError(i18n.t('form.errors.onlyDigits'))
                    .min(0, i18n.t('form.errors.greaterZero'))
                    .test(
                        'is-decimal',
                        i18n.t('form.errors.twoDigitsAfterComma'),
                        (val?: number) => {
                            if (val) {
                                return regexHelper.twoDigisAfterComma.test(val.toString());
                            }
                            return true;
                        }
                    ),

                priceLevels: yup.array().of(
                    yup.object().shape({
                        price: yup
                            .number()
                            .typeError(i18n.t('form.errors.onlyDigits'))
                            .min(0, i18n.t('form.errors.greaterZero'))
                            .test(
                                'is-decimal',
                                i18n.t('form.errors.twoDigitsAfterComma'),
                                (val?: number) => {
                                    if (val) {
                                        return regexHelper.twoDigisAfterComma.test(val.toString());
                                    }
                                    return true;
                                }
                            ),
                    })
                ),
                ...(restaurant.isOrderInAdvance
                    ? {
                          orderInAdvanceSameDay: sameDayOfferSchema.nullable(),
                          // Ordering windows belong to the whole Custom configuration, not per size -
                          // no daySettings requirement here, only the root orderInAdvanceCustom checks it.
                          orderInAdvanceCustom: offerSchema.nullable(),
                      }
                    : {}),
            })
        ),
        priceLevels: yup
            .array()
            .of(
                yup.object().shape({
                    price: yup
                        .number()
                        .typeError(i18n.t('form.errors.onlyDigits'))
                        .min(0, i18n.t('form.errors.greaterZero'))
                        .test(
                            'is-decimal',
                            i18n.t('form.errors.twoDigitsAfterComma'),
                            (val?: number) => {
                                if (val) {
                                    return regexHelper.twoDigisAfterComma.test(val.toString());
                                }
                                return true;
                            }
                        ),
                })
            )
            .test('not-above-regular-price', '', function (levels) {
                const regularPrice = Number(this.parent?.price);
                if (!menu.catalogData?.catalogId || !levels?.length || Number.isNaN(regularPrice)) {
                    return true;
                }

                const errors = levels
                    .map((level, index) =>
                        Number(level?.price) > regularPrice
                            ? this.createError({
                                  path: `priceLevels[${index}].price`,
                                  message: i18n.t('form.errors.priceLevelAboveRegularPrice', {
                                      price: regularPrice.toFixed(2),
                                  }),
                              })
                            : null
                    )
                    .filter((error): error is yup.ValidationError => error !== null);

                return errors.length ? new yup.ValidationError(errors) : true;
            }),
        externalId: yup
            .number()
            .nullable()
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero'))
            .max(
                Number.MAX_SAFE_INTEGER,
                i18n.t('form.errors.lessThan', { value: Number.MAX_SAFE_INTEGER })
            ),
    });

    function formDisabled(isDirty: boolean) {
        return !props.forCopy && !isDirty;
    }

    return (
        <>
            <Dialog
                maxWidth="lg"
                open={props.isOpen}
                onClick={preventProp}
                onClose={onCancel}
                aria-labelledby="form-dialog-title"
                className={classes.dialog}
                PaperProps={{
                    style: {
                        position: 'absolute',
                        top: 30,
                        margin: 0,
                    },
                }}
            >
                {props.restaurantName && (
                    <Box
                        bgcolor={alpha(theme.palette.primary.main, 0.4)}
                        color={theme.palette.primary.dark}
                        padding="5px 0 5px 17px"
                        fontWeight={600}
                    >
                        <RestaurantIcon style={{ marginRight: 5, height: 15 }} />
                        {props.restaurantName}
                    </Box>
                )}
                <DialogTitle
                    id="form-dialog-title"
                    className={clsx(classes.dialogTitle, {
                        [classes.dialogTitleExtended]: showAdditionalForm,
                    })}
                    ref={title}
                >
                    {props.disabled ? props.dish.name : i18n.t('common.editItem')}{' '}
                    {/* <div onClick={() => setShowAdditionalForm(true)}>
                        {advancedSettingsHasError && !showAdditionalForm ? (
                            <div className={classes.hasError}>
                                <h5>
                                    <img src={images.icons.settingsError} alt="settings" />
                                    {i18n.t('common.advancedSettings')}
                                </h5>
                                <span>{i18n.t('form.errors.hasIncorrectData')}</span>
                            </div>
                        ) : (
                            <h5>
                                <img src={images.icons.settings} alt="settings" />
                                {i18n.t('common.advancedSettings')}
                            </h5>
                        )}
                    </div> */}
                </DialogTitle>
                <Tabs
                    value={tabActive}
                    onChange={handleChangeTab}
                    aria-label="wrapped label tabs example"
                    indicatorColor="primary"
                    textColor="primary"
                    style={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                        paddingLeft: 25,
                    }}
                >
                    <Tab value={DishManagementTabs.MAIN} label={i18n.t('navigationTabs.main')} />
                    <Tab
                        value={DishManagementTabs.ADVANCED}
                        label={i18n.t('navigationTabs.advanced')}
                    />
                    {(getAllPricingLevels(restaurant, isCatalog).length > 0 ||
                        restaurant.isOrderInAdvance) && (
                        <Tab
                            value={DishManagementTabs.PRICE_MANAGEMENT}
                            label={i18n.t('navigationTabs.priceManagement')}
                        />
                    )}
                </Tabs>
                <DialogContent>
                    <Formik
                        enableReinitialize={true}
                        initialValues={initialFormValues}
                        validationSchema={validationSchema}
                        onSubmit={async (values, { resetForm }) => {
                            const parsed = Number(values.externalId);
                            const externalId =
                                Number.isInteger(parsed) && parsed >= 0 ? parsed : null;

                            childRef?.current
                                ?.uploadImage()
                                .then(async (photo) => {
                                    values.photo = photo;
                                    let price = values.price.replaceAll(' ', '');
                                    const model: DishCreateModel = {
                                        name: trimLeftAndRight(values.name),
                                        shortName: values.shortName,
                                        description: trimLeftAndRight(values.description),
                                        shortDescription: trimLeftAndRight(values.shortDescription),
                                        price: +price,
                                        photo: values.photo,
                                        sectionId: values.section,
                                        addToGallery: values.addToGallery,
                                        categoryId: values.category,
                                        allergens: values.allergens,
                                        ingredients: values.ingredients,
                                        primeCost: +values.primeCost,
                                        dineInTax: values.dineInTax,
                                        homeDeliveryTax: values.homeDeliveryTax,
                                        takeAwayTax: values.takeAwayTax,
                                        barcode: values.barcode,
                                        stockQuantity: values.stockQuantity,
                                        minimumStock: values.minimumStock,
                                        notFood: values.notFood,
                                        color: props.dish.color,
                                        spicinessLevel: values.spicinessLevel,
                                        diet: values.diet,
                                        dietaryLaw: values.dietaryLaw,
                                        labels: values.labels,
                                        size: values.size,
                                        priceLevels: values.priceLevels.map((pl, i) => {
                                            return {
                                                priceLevelId: pl.priceLevelId,
                                                price: !!!props.dish?.id
                                                    ? +values.price
                                                    : +pl.price,
                                            };
                                        }),
                                        additionalSizes: values.additionalSizes.map((x, i) => {
                                            const initialSize =
                                                initialFormValues.additionalSizes[i];
                                            return {
                                                ...x,
                                                price: +x.price,
                                                priceLevels: x.priceLevels.map((pl) => ({
                                                    priceLevelId: pl.priceLevelId,
                                                    price: !!initialSize ? +pl.price : +x.price,
                                                })),
                                                orderInAdvanceSameDay: serializeOffer(
                                                    x.orderInAdvanceSameDay,
                                                    false,
                                                    values.orderInAdvanceSameDay
                                                        ?.orderingOffsetHours
                                                ),
                                                orderInAdvanceCustom: serializeOffer(
                                                    x.orderInAdvanceCustom,
                                                    true
                                                ),
                                            };
                                        }),
                                        isCustomPrice: values.isCustomPrice,
                                        externalId: externalId,
                                        supplierName: values.supplierName,
                                        orderInAdvanceSameDay: serializeOffer(
                                            values.orderInAdvanceSameDay,
                                            false
                                        ),
                                        orderInAdvanceCustom: serializeOffer(
                                            values.orderInAdvanceCustom,
                                            true
                                        ),
                                    };
                                    await props.action(model, props.dish.id);
                                })
                                .then(() => {
                                    resetForm();
                                    props.onClose();
                                })
                                .catch((error) => alert(error));
                        }}
                    >
                        {({ submitForm, dirty }) => (
                            <Form className={classes.root} style={{ textAlign: 'start' }}>
                                <>
                                    <Box
                                        display={
                                            tabActive === DishManagementTabs.MAIN ? 'block' : 'none'
                                        }
                                    >
                                        <MainTab
                                            sections={props.sections}
                                            disabled={props.disabled}
                                            restaurantName={props.restaurantName}
                                            showAdditionalForm={showAdditionalForm}
                                            title={title}
                                            showAddIngredientsModal={showAddIngredientsModal}
                                            setShowAddIngredientsModal={setShowAddIngredientsModal}
                                            setTextToEdit={setTextToEdit}
                                            setValueName={setValueName}
                                            setShowEditor={setShowEditor}
                                            childRef={childRef}
                                            isDeniedTaxSetup={isDeniedTaxSetup}
                                            isFieldDisabled={isFieldDisabled}
                                            defaultIngredients={props.dish.ingredients}
                                        />
                                    </Box>
                                    <Box
                                        display={
                                            tabActive === DishManagementTabs.ADVANCED
                                                ? 'block'
                                                : 'none'
                                        }
                                    >
                                        <AdvancedTab
                                            disabled={props.disabled}
                                            isFieldDisabled={isFieldDisabled}
                                        />
                                    </Box>
                                    <Box
                                        display={
                                            tabActive === DishManagementTabs.PRICE_MANAGEMENT
                                                ? 'block'
                                                : 'none'
                                        }
                                        marginBottom={2}
                                    >
                                        <PriceManagementTab isFieldDisabled={isFieldDisabled} />
                                    </Box>
                                    <Box className={classes.bottomPanel}>
                                        <Box
                                            className={classes.bottomContainer}
                                            flexGrow={0.9}
                                            justifyContent="flex-start"
                                        ></Box>
                                        <Box display="flex" flexDirection="row">
                                            {isFieldDisabled('save') ? (
                                                <Button
                                                    type="button"
                                                    color="primary"
                                                    onClick={onCancel}
                                                >
                                                    {i18n.t('button.close')}
                                                </Button>
                                            ) : (
                                                <>
                                                    <Button
                                                        type="button"
                                                        color="primary"
                                                        onClick={onCancel}
                                                        className={clsx(classes.mRight10, {
                                                            [classes.smallFont]: lang === 'de',
                                                        })}
                                                    >
                                                        {i18n.t('button.cancel')}
                                                    </Button>
                                                    <Button
                                                        disabled={formDisabled(dirty)}
                                                        onClick={submitForm}
                                                        variant="contained"
                                                        color="primary"
                                                    >
                                                        {i18n.t('button.save')}
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </>
                                <HandleFormikHelper action={handleShowAdvancedSettingsError} />
                                <FormikOrderInAdvanceErrorHandler onTabChange={setTab} />
                                <FormikDebugErrors />
                                <SetFieldText field={valueName} text={textToEdit} />
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
            <TextEditor
                isOpen={showEditor}
                text={textToEdit}
                onClose={() => setShowEditor(false)}
                onSave={saveTextFromEditor}
            />
        </>
    );
}

function FormikDebugErrors() {
    const { errors, isValid } = useFormikContext();
    useEffect(() => {
        console.log('[FormikDebug] isValid:', isValid);
        console.log('[FormikDebug] errors:', JSON.stringify(errors, null, 2));
    });
    return null;
}

function FormikOrderInAdvanceErrorHandler({
    onTabChange,
}: {
    onTabChange: (tab: DishManagementTabs) => void;
}) {
    const { errors, submitCount } = useFormikContext<any>();

    useEffect(() => {
        if (!submitCount) return;

        const errorPaths: string[] = [];
        const collectPaths = (value: any, path: string) => {
            if (typeof value === 'string') {
                errorPaths.push(path);
                return;
            }
            if (!value || typeof value !== 'object') return;
            Object.keys(value).forEach((key) =>
                collectPaths(value[key], path ? `${path}.${key}` : key)
            );
        };
        collectPaths(errors, '');

        const firstOrderInAdvanceError = errorPaths.find(
            (path) => path.startsWith('orderInAdvance') || path.startsWith('additionalSizes')
        );
        if (!firstOrderInAdvanceError) return;

        onTabChange(DishManagementTabs.PRICE_MANAGEMENT);
        window.setTimeout(() => {
            const field = document.querySelector<HTMLElement>(
                `[name="${firstOrderInAdvanceError}"]`
            );
            if (field) {
                field.focus();
                field.scrollIntoView({ block: 'center' });
                return;
            }

            const addWindowButton = Array.from(document.querySelectorAll('button')).find((button) =>
                button.textContent?.includes(i18n.t('common.addWindow'))
            );
            addWindowButton?.focus();
        }, 0);
    }, [errors, submitCount, onTabChange]);

    return null;
}

function HandleFormikHelper({ action }: { action: (hasError: boolean) => void }) {
    const { errors } = useFormikContext();
    const restaurant = useAppSelector((state) => state.restaurantDetails?.restaurantInformation);
    const isCoolBox = restaurant?.coolBoxData?.isCoolBox ?? false;
    useEffect(() => {
        const hasError =
            'barcode' in errors ||
            'primeCost' in errors ||
            'stockQuantity' in errors ||
            'minimumStock' in errors ||
            ('externalId' in errors && isCoolBox);
        action(hasError);
    }, [errors]);
    return null;
}
